import { useMemo } from "react";
import { Eye, Mail, X, RefreshCcw } from "lucide-react";
import { Detail } from "./shared";

const ENQUIRY_STATUSES = ["new", "read", "replied", "archived"];

const EnquiryStatusPill = ({ status }) => {
  const map = {
    new: "border-sky-700/40 text-sky-700 bg-sky-50/40",
    read: "border-[var(--ld-gold-deep)]/60 text-[var(--ld-gold-deep)]",
    replied: "border-emerald-700/40 text-emerald-700",
    archived: "border-[var(--ld-ink)]/30 text-[var(--ld-ink-muted)]",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 text-[10px] tracking-[0.18em] uppercase border ${
        map[status] || map.new
      }`}
    >
      {status || "new"}
    </span>
  );
};

const EnquiryPanel = ({
  enquiries,
  loading,
  onView,
  onStatus,
  onDelete,
  onRefresh,
}) => {
  const counts = useMemo(() => {
    const c = { all: enquiries.length, new: 0, read: 0, replied: 0, archived: 0 };
    enquiries.forEach((e) => {
      const s = e.status || "new";
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [enquiries]);

  return (
    <div data-testid="section-enquiry">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Inbox
          </span>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Enquiry
          </h2>
          <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
            Every enquiry submitted via the Contact form lands here. Mark each
            one as read, replied, or archived to keep the inbox clean.
          </p>
        </div>
        <button
          data-testid="enquiry-refresh"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
        <span className="px-2 py-1 border border-[var(--ld-border)]">
          Total · {counts.all}
        </span>
        <span className="px-2 py-1 border border-sky-700/30 text-sky-700">
          New · {counts.new}
        </span>
        <span className="px-2 py-1 border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]">
          Read · {counts.read}
        </span>
        <span className="px-2 py-1 border border-emerald-700/30 text-emerald-700">
          Replied · {counts.replied}
        </span>
        <span className="px-2 py-1 border border-[var(--ld-ink)]/20">
          Archived · {counts.archived}
        </span>
      </div>

      <div className="mt-8 border border-[var(--ld-border)]/80 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="enquiry-table">
            <thead className="bg-[var(--ld-ink)]/[0.03] text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
              <tr>
                <th className="text-left font-accent font-normal px-4 py-3">
                  Received
                </th>
                <th className="text-left font-accent font-normal px-4 py-3">
                  From
                </th>
                <th className="text-left font-accent font-normal px-4 py-3">
                  Subject
                </th>
                <th className="text-left font-accent font-normal px-4 py-3">
                  Status
                </th>
                <th className="text-right font-accent font-normal px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--ld-border)]/70">
              {loading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-[var(--ld-ink-muted)]"
                  >
                    Loading enquiries…
                  </td>
                </tr>
              )}
              {!loading && enquiries.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-[var(--ld-ink-muted)]"
                  >
                    No enquiries yet.
                  </td>
                </tr>
              )}
              {enquiries.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`enquiry-row-${e.id}`}
                  className="hover:bg-[var(--ld-ink)]/[0.02] transition-colors"
                >
                  <td className="px-4 py-3 text-[var(--ld-ink-muted)] whitespace-nowrap">
                    {e.submitted_at
                      ? new Date(e.submitted_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--ld-ink)]">
                      {e.name}
                    </div>
                    <div className="text-xs text-[var(--ld-ink-muted)]">
                      {e.email}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink)] max-w-md truncate">
                    {e.subject}
                  </td>
                  <td className="px-4 py-3">
                    <EnquiryStatusPill status={e.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`enquiry-view-${e.id}`}
                        onClick={() => {
                          onView(e);
                          if ((e.status || "new") === "new") {
                            onStatus(e.id, "read");
                          }
                        }}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
                      >
                        <Eye size={13} strokeWidth={1.6} />
                        Open
                      </button>
                      <select
                        data-testid={`enquiry-status-${e.id}`}
                        value={e.status || "new"}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          if (v === "__delete__") {
                            if (window.confirm("Delete this enquiry? This cannot be undone.")) {
                              onDelete(e.id);
                            }
                            return;
                          }
                          onStatus(e.id, v);
                        }}
                        className="bg-transparent border border-[var(--ld-ink)]/30 text-[11px] tracking-[0.12em] uppercase px-2 py-1.5 focus:outline-none focus:border-[var(--ld-gold-deep)]"
                      >
                        {ENQUIRY_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                        <option value="__delete__" className="text-red-700">
                          delete
                        </option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const EnquiryDetailDrawer = ({ row, onClose, onStatus, onDelete }) => {
  if (!row) return null;
  return (
    <div
      role="dialog"
      data-testid="enquiry-detail-drawer"
      className="fixed inset-0 z-50 flex"
    >
      <div
        className="flex-1 bg-[var(--ld-ink)]/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="w-full max-w-xl bg-[var(--ld-ivory)] overflow-y-auto p-8 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
              Enquiry
            </span>
            <h3 className="font-serif-display text-2xl mt-2 text-[var(--ld-ink)]">
              {row.subject}
            </h3>
            <div className="mt-2">
              <EnquiryStatusPill status={row.status} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] p-1"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        <dl className="mt-6">
          <Detail label="From" value={row.name} />
          <Detail label="Email" value={row.email} />
          <Detail
            label="Received"
            value={
              row.submitted_at
                ? new Date(row.submitted_at).toLocaleString()
                : ""
            }
          />
        </dl>

        <div className="mt-6">
          <p className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-ink-muted)] mb-2">
            Message
          </p>
          <div className="border border-[var(--ld-border)]/80 p-5 text-[14px] leading-[1.75] text-[var(--ld-ink)] whitespace-pre-wrap">
            {row.message}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={`mailto:${row.email}?subject=Re: ${encodeURIComponent(
              row.subject
            )}`}
            data-testid="enquiry-reply"
            onClick={() => onStatus(row.id, "replied")}
            className="inline-flex items-center gap-2 h-10 px-5 text-[12px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors"
          >
            <Mail size={14} strokeWidth={1.6} />
            Reply by email
          </a>
          <button
            data-testid="enquiry-archive"
            onClick={() => onStatus(row.id, "archived")}
            className="inline-flex items-center h-10 px-5 text-[12px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
          >
            Archive
          </button>
          <button
            data-testid="enquiry-delete"
            onClick={() => {
              if (window.confirm("Delete this enquiry permanently?")) {
                onDelete(row.id);
              }
            }}
            className="inline-flex items-center h-10 px-5 text-[12px] tracking-[0.18em] uppercase text-red-700 border border-red-700/30 hover:bg-red-700/5 transition-colors ml-auto"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnquiryPanel;
